import { config } from "../config/config";
import { getBuyAdminAuthHeaders as getAuthHeaders } from "./buyAdminAuth";

export interface BuyEmailNotification {
  id: string;
  order_id: number;
  order_number: string | null;
  user_id: string | null;
  user_name: string | null;
  notification_type: string;
  order_status_in_email: string;
  trigger_source: string;
  channel: "email" | "sms" | string;
  recipient_email: string;
  recipient_phone: string | null;
  subject: string;
  provider: string;
  ses_message_id: string | null;
  provider_message_id: string | null;
  delivery_status: string;
  error_message: string | null;
  message_body: string | null;
  email_html: string | null;
  email_text: string | null;
  mobile_links: unknown[];
  attachments: unknown[];
  attachment_types: string[];
  attachment_count: number;
  has_mobile_link: boolean;
  has_pdf_attachment: boolean;
  provider_metadata: Record<string, unknown>;
  created_at: string;
}

export interface BuyEmailNotificationsResponse {
  data: BuyEmailNotification[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
  };
  counts: {
    sent: number;
    failed: number;
    skipped: number;
  };
}

export interface BuyEmailNotificationStatusesResponse {
  data: string[];
}

export interface BuyEmailNotificationFilters {
  limit: number;
  offset: number;
  orderId?: string;
  userId?: string;
  notificationType?: string;
  deliveryStatus?: string;
  channel?: string;
  recipient?: string;
  orderStatusInEmail?: string;
  hasPdfAttachment?: string;
  hasMobileLink?: string;
  attachmentType?: string;
  from?: string;
  to?: string;
}

export interface BuyOrderFinancial {
  order_id: number;
  order_number: string | null;
  user_id: string;
  user_name: string | null;
  user_email: string | null;
  user_phone: string | null;
  order_status: string;
  seller_status: string | null;
  payment_status: string | null;
  ev_order_id: string | null;
  payment_intent_id: string | null;
  created_at: string;
  tickets_in_hand_on: string | null;
  event_name?: string | null;
  event_date?: string | null;
  event_time?: string | null;
  venue_name?: string | null;
  section?: string | null;
  row?: string | null;
  seats?: string | null;
  item_count: number;
  ticket_quantity: number;
  customer_subtotal: number;
  customer_tax: number;
  customer_total: number;
  customer_savings?: number;
  refunded_amount: number;
  te_ticket_cost: number;
  te_tax_total: number;
  te_service_fee_total: number;
  qa_pay_to_te_total: number;
  stripe_processing_fee_total: number;
  gross_spread: number;
  net_after_stripe: number;
  refund_adjusted_net_after_stripe: number;
  missing_te_price_count: number;
  has_te_financials_gap: boolean;
}

export interface BuyOrderFinancialsSummary {
  order_count: number;
  ticket_quantity: number;
  customer_subtotal: number;
  customer_tax: number;
  customer_total: number;
  customer_savings?: number;
  refunded_amount: number;
  net_customer_collected: number;
  te_ticket_cost: number;
  te_tax_total: number;
  te_service_fee_total: number;
  qa_pay_to_te_total: number;
  stripe_processing_fee_total: number;
  gross_spread: number;
  net_after_stripe: number;
  refund_adjusted_net_after_stripe: number;
  gap_order_count: number;
  history_item_count?: number;
  history_ticket_quantity?: number;
  history_event_count?: number;
  history_total_spent?: number;
  history_total_savings?: number;
}

export interface BuyOrderFinancialsResponse {
  data: BuyOrderFinancial[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
  };
  summary: BuyOrderFinancialsSummary;
  options: {
    order_statuses: string[];
    payment_statuses: string[];
  };
}

export interface BuyFulfillmentOrder {
  id: string;
  order_id: number;
  item_id: number;
  order_number: string | null;
  status: string;
  raw_status: string | null;
  purchased_at: string;
  fulfillment_type: string | null;
  event_name: string | null;
  performer: string | null;
  event_date: string | null;
  event_occurs_at_local: string | null;
  event_time: string | null;
  venue: string | null;
  league: string | null;
  section: string | null;
  row: string | null;
  seats: string | null;
  quantity: number;
  price_per_ticket: number;
  total_ticket_cost: number;
  total_spent: number;
  total_saved: number;
  customer_name: string | null;
  customer_email: string | null;
  card_last4: string | null;
  in_hand_date: string | null;
  automatic_transfer: boolean;
  member_id: string | null;
  member: { id: string; name: string | null; email: string | null } | null;
}

export interface BuyFulfillmentCounts {
  pending: number;
  confirmed: number;
  fulfilled: number;
  cancelled: number;
  refunded: number;
  all: number;
}

export interface BuyFulfillmentFilters {
  status?: string;
  search?: string;
  delivery_type?: string;
  date_from?: string;
  date_to?: string;
  in_hand_from?: string;
  in_hand_to?: string;
  sort_by?: string;
  sort_dir?: string;
  page?: number;
  limit?: number;
}

export interface BuyFulfillmentDeliveryTypeOption {
  value: string;
  label: string;
  count: number;
}

export interface BuyFulfillmentResponse {
  orders: BuyFulfillmentOrder[];
  total: number;
  page: number;
  limit: number;
  counts: BuyFulfillmentCounts;
  filters?: {
    delivery_types?: BuyFulfillmentDeliveryTypeOption[];
  };
}

export interface BuyAnalyticsKeyMetrics {
  active_members: number;
  revenue: number;
  tickets_sold: number;
  member_savings: number;
  new_signups: number;
  changes: {
    active_members: number;
    revenue: number;
    tickets_sold: number;
    member_savings: number;
    new_signups: number;
  };
}

export interface BuyAnalyticsTimingBucket {
  bucket: string;
  count: number;
  pct: number;
  color: string;
}

export interface BuyAnalyticsGrowthPoint {
  month: string;
  revenue: number;
  members: number;
}

export interface BuyAnalyticsCohortRow {
  cohort: string;
  m1: number;
  m2: number;
  m3: number;
  m6: number;
  m12: number;
}

export interface BuyAnalyticsBehaviorRow {
  category: string;
  count: number;
  pct: number;
}

export interface BuyAnalyticsTopEvent {
  event: string;
  tickets_sold: number;
  revenue: number;
  member_savings: number;
  avg_price: number;
}

export interface BuyAnalyticsResponse {
  window: {
    start: string;
    end: string;
    previousStart: string;
    previousEnd: string;
  };
  key_metrics: BuyAnalyticsKeyMetrics;
  purchase_timing: {
    total: number;
    buckets: BuyAnalyticsTimingBucket[];
  };
  revenue_growth: BuyAnalyticsGrowthPoint[];
  cohort_retention: BuyAnalyticsCohortRow[];
  purchase_behavior: BuyAnalyticsBehaviorRow[];
  top_events: BuyAnalyticsTopEvent[];
}

export interface BuyAnalyticsFilters {
  timeframe?: string;
  from?: string;
  to?: string;
}

export interface BuyPricingData {
  totalMarkup: number;
  avgMarkupDollar: number;
  avgMarkupPercent: number;
  avgCompetitorDiff: number;
  competitivenessScore: number;
  totalTransactions: number;
  totalSavingsToMembers: number;
  totalTickets: number;
  savingsShare: number;
  markupShare: number;
}

export interface BuyPricingLeagueRow {
  league: string;
  avgMarkup: number;
  avgCompDiff: number;
  transactions: number;
  competitiveRate: number;
}

export interface BuyPricingTrendPoint {
  month: string;
  avgMarkup: number;
  competitorDiff: number;
  volume: number;
}

export interface BuyPricingIntelligenceResponse {
  window: {
    start: string;
    end: string;
  };
  pricingData: BuyPricingData;
  markupByLeague: BuyPricingLeagueRow[];
  marginTrendData: BuyPricingTrendPoint[];
  insights: {
    bestLeague: string;
    highestMarkupLeague: string;
    lowestCompetitiveLeague: string;
  };
}

export interface BuyPricingIntelligenceFilters {
  from?: string;
  to?: string;
}

export interface BuyRevenueMonth {
  month: string;
  mrr: number;
  arr: number;
  newMrr: number;
  churnMrr: number;
  netMrr: number;
  members: number;
  ticketFees: number;
  premiumFeatures: number;
  subscriptions: number;
  other: number;
  grossRevenue: number;
  memberSavings: number;
  orders: number;
}

export interface BuyRevenueBreakdownItem {
  source: string;
  amount: number;
  pct: number;
}

export interface BuyRevenueReportsResponse {
  window: {
    start: string;
    end: string;
  };
  currentMonth: BuyRevenueMonth;
  previousMonth: BuyRevenueMonth;
  mrrGrowth: number;
  monthlyData: BuyRevenueMonth[];
  revenueBreakdown: BuyRevenueBreakdownItem[];
}

export interface BuyRevenueReportsFilters {
  from?: string;
  to?: string;
}

export interface BuyAllPurchase {
  id: string;
  order_id: number;
  member: string;
  memberId: string | null;
  email: string | null;
  event: string;
  tickets: number;
  total: number;
  savings: number;
  status: string;
  order_status: string | null;
  tevo_status: string | null;
  date: string;
  tickets_in_hand_on: string | null;
  delivery_type: string | null;
  event_occurs_at: string | null;
  upcoming_event_date: string | null;
  payment_method: string;
}

export interface BuyAllPurchasesSummary {
  total_purchases: number;
  total_revenue: number;
  member_savings: number;
  fulfillment_rate: number;
}

export interface BuyAllPurchasesResponse {
  data: BuyAllPurchase[];
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
  summary: BuyAllPurchasesSummary;
  filters?: {
    tevo_statuses?: string[];
  };
}

export interface BuyAllPurchasesFilters {
  search?: string;
  status?: string;
  tevo_status?: string;
  from?: string;
  to?: string;
  sort_by?: string;
  sort_dir?: string;
  prioritize_upcoming?: boolean;
  page?: number;
  limit?: number;
}

export interface BuyFailedPurchase {
  id: string;
  order_id: number;
  member: string;
  memberId: string | null;
  email: string | null;
  event: string;
  event_date: string | null;
  event_occurs_at_local: string | null;
  tickets: number;
  attempted_amount: number;
  card_last4: string;
  card_brand: string;
  failure_reason: string;
  failure_source: string | null;
  timestamp: string;
  retry_count: number;
}

export interface BuyFailedPurchasesSummary {
  total_failed_attempts: number;
  potential_lost_revenue: number;
  unique_members_affected: number;
  last_24_hours: number;
}

export interface BuyFailedPurchasesResponse {
  data: BuyFailedPurchase[];
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
  summary: BuyFailedPurchasesSummary;
}

export interface BuyFailedPurchasesFilters {
  search?: string;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
}

export interface BuyRenewalFailure {
  id: string;
  member: string;
  memberId: string | null;
  email: string | null;
  phone: string | null;
  failed_renewal_date: string | null;
  last_successful_payment: string | null;
  amount_due: number;
  current_status: "active" | "paused" | "churned" | string;
  card_last4: string;
  card_brand: string;
  failure_reason: string;
  retry_attempts: number;
  days_overdue: number;
}

export interface BuyRenewalFailuresSummary {
  failed_renewals: number;
  mrr_at_risk: number;
  already_churned: number;
  still_recoverable: number;
}

export interface BuyRenewalFailuresResponse {
  data: BuyRenewalFailure[];
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
  summary: BuyRenewalFailuresSummary;
}

export interface BuyRenewalFailuresFilters {
  search?: string;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
}

export interface BuyOrderFinancialsFilters {
  limit: number;
  offset: number;
  order?: string;
  customer?: string;
  userId?: string;
  orderStatus?: string;
  paymentStatus?: string;
  from?: string;
  to?: string;
  completedOnly?: string;
}

export interface BuyAdminEventMarkup {
  event_id: number;
  event_name: string;
  external_id?: string | null;
  event_time: string | null;
  event_date: string | null;
  occurs_at: string | null;
  occurs_at_local: string | null;
  place: string;
  venue_id: number | null;
  venue_name: string;
  performer_names: string[];
  markup_percentage: number;
  source: string | null;
  status: string | null;
  last_markup_change_at: string | null;
  last_markup_changed_by: string | null;
  slot?: number;
  selected_listing_id?: number | null;
  selected_listing?: BuyAdminCarouselListingSnapshot | null;
  competitor_prices?: Partial<Record<BuyAdminCarouselCompetitor, number>>;
}

export interface BuyAdminEventsMarkupSummary {
  event_count: number;
  date_tbd_count: number;
  average_markup_percentage: number;
}

export interface BuyAdminEventsMarkupResponse {
  data: BuyAdminEventMarkup[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
  };
  summary: BuyAdminEventsMarkupSummary;
}

export interface BuyAdminEventsMarkupFilters {
  limit: number;
  offset: number;
  search?: string;
  from?: string;
  to?: string;
}

export interface BuyAdminLandingComparisonSelectionResponse {
  event_ids: number[];
  data: BuyAdminEventMarkup[];
}

export type BuyAdminCarouselCompetitor =
  | "ticketmaster"
  | "seatgeek"
  | "vivid_seats"
  | "stubhub"
  | "tickpick"
  | "gametime";

export interface BuyAdminCarouselListingSnapshot {
  listing_id: number;
  section: string | null;
  row: string | null;
  seats: string | null;
  quantity: number;
  te_price: number;
  quickasyst_price: number;
}

export interface BuyAdminCarouselConfigurationInput {
  eventId: number;
  listingId: number;
  competitorPrices: Partial<Record<BuyAdminCarouselCompetitor, number>>;
}

export interface BuyAdminEventMarkupAudit {
  id: number;
  event_id: number;
  old_markup_percentage: number;
  new_markup_percentage: number;
  changed_by: string;
  changed_role: string | null;
  note: string | null;
  created_at: string;
}

export interface BuyAdminEventMarkupAuditResponse {
  data: BuyAdminEventMarkupAudit[];
}

export interface BuyAdminEventListing {
  id?: number | string;
  listing_id?: number | string;
  event_id?: number | string;
  performance_id?: number | string | null;
  configuration_id?: number | string | null;
  venue_id?: number | string | null;
  price?: number | string | null;
  retail_price?: number | string | null;
  quantity?: number | string | null;
  available_quantity?: number | string | null;
  section?: string | null;
  row?: string | null;
  seats?: string | null;
  event_markup_percentage?: number | null;
  listing_markup_percentage?: number | null;
  listing_price_adjustment_amount?: number | null;
  price_adjustment_amount?: number | null;
  markup_percentage?: number | null;
  effective_markup_percentage?: number | null;
  display_price?: number | null;
  customer_price?: number | null;
  listing_markup_override_applied?: boolean;
  [key: string]: any;
}

export interface BuyAdminEventListingsResponse {
  data?: {
    event?: BuyAdminEventMarkup;
    listings?: BuyAdminEventListing[];
    total_entries?: number;
  };
  event?: BuyAdminEventMarkup;
  listings?: BuyAdminEventListing[];
  total_entries?: number;
}

export interface BuyAdminSubscription {
  id: number;
  subscription_type: string;
  amount_cents: number;
  duration_days: number;
  currency: string;
  description: string | null;
  is_active: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface BuyAdminSubscriptionInput {
  subscription_type: string;
  amount_cents: number;
  duration_days: number;
  currency?: string;
  description?: string;
  is_active?: boolean;
}

export interface BuyAdminSubscriptionsResponse {
  data: BuyAdminSubscription[];
}

export interface BuyAdminPerformerTeamSearchResult {
  id: number;
  name: string;
  slug: string | null;
  url: string | null;
  category_id: number | null;
  category_name: string | null;
  is_team: boolean | null;
}

export interface BuyAdminPerformerTeamsSearchResponse {
  data: BuyAdminPerformerTeamSearchResult[];
}

export interface BuyAdminPerformerTeamsListResponse {
  data: BuyAdminPerformerTeamSearchResult[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
  };
}

export interface BuyAdminPerformerTeamsBulkUpdateResponse {
  data: {
    updated: number;
    failed: Array<{ id: number; reason: string }>;
  };
}

export interface BuyAdminSalesRep {
  sr_id: string;
  sr_first_name: string | null;
  sr_last_name: string | null;
  sr_email: string | null;
}

export interface BuyAdminManagedUser {
  sales_rep_id?: string | null;
  sales_rep_name?: string | null;
  membership_end_at?: string | null;
  /** Paid state OR a live, non-deactivated admin grant, computed by the backend. */
  effective_premium?: boolean | null;
  /** Comp right now: dashboard-created until ended, or extended until the date. */
  has_live_admin_grant?: boolean;
  /** Has ever been Comp. */
  had_admin_grant?: boolean;
  can_extend_membership?: boolean;
  id: string;
  sell_user_id: string | null;
  email: string;
  first_name: string | null;
  last_name: string | null;
  full_name: string;
  phone_number: string | null;
  street_address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  role: string | null;
  membership_status: string | null;
  subscription_type: string | null;
  is_active: boolean;
  is_deactivated: boolean;
  deactivated_at: string | null;
  is_verified: boolean;
  /** True when the account was created from the admin dashboard (Add User). */
  created_by_admin?: boolean;
  created_at: string | null;
  last_login_at: string | null;
  last_activity_at: string | null;
  order_count: number;
  lifetime_spend: number;
  lifetime_savings: number;
  affiliate_id: string | null;
  affiliate_status: string | null;
  referral_code: string | null;
}

export interface BuyAdminCreateMemberInput {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  password: string;
  referralCode?: string;
  /** Sell account created first, the same way Sell Add User creates it. */
  sellUserId?: string;
  /** Check every field without saving anything. */
  validateOnly?: boolean;
}

/** Same input Sell Add User sends to adminCreateUser and /api/staffCreateUser. */
export interface SellStaffCreateUserInput {
  firstName: string;
  lastName: string;
  emailId: string;
  phoneNumber: string;
  countryCode: string;
  clientType: string;
  password: string;
  percentage: number;
  businessName: string | null;
  salesRepId: string | null;
  affiliateId: string | null;
  teams: string[];
}

export interface BuyAdminCreateMemberResponse {
  message: string;
  user: { u_id: string };
}

export interface BuyAdminUserPaymentMethod {
  user_id: string;
  card_brand: string | null;
  card_last4: string | null;
  card_expiry: string | null;
  cardholder_name: string | null;
  updated_at: string | null;
}

export interface BuyAdminAffiliate {
  id: string;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  phone_number: string | null;
  organization: string | null;
  affiliate_type: string;
  referral_code: string;
  referral_link: string;
  url_suffix: string | null;
  referred_by_affiliate_id: string | null;
  created_by_user_id: string | null;
  status: string;
  created_at: string;
  referral_count: number;
  converted_referral_count: number;
  referral_earnings_cents: number;
  referral_earnings: number;
  affiliate_earnings_cents: number;
  affiliate_earnings: number;
  direct_affiliate_earnings_cents: number;
  direct_affiliate_earnings: number;
  indirect_affiliate_earnings_cents: number;
  indirect_affiliate_earnings: number;
  earnings_cents: number;
  earnings: number;
  app_user_id: string | null;
}

export interface BuyAdminAffiliateReferralUser {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  full_name: string;
  phone_number: string | null;
  role: string | null;
  membership_status: string | null;
  is_active: boolean;
  is_verified: boolean;
  created_at: string | null;
  referral_id: string;
  referral_code: string;
  referral_status: string;
  captured_at: string | null;
  linked_at: string | null;
  converted_at: string | null;
}

export interface BuyAdminAffiliateEmailDelivery {
  status: "sent" | "skipped" | "failed";
  message: string;
  toEmail: string;
  messageId?: string;
}

export interface BuyAdminUserAdminSummary {
  customer_count?: number;
  premium_count?: number;
  active_count?: number;
  new_signups?: number;
  affiliate_count?: number;
  active_affiliate_count?: number;
}

export interface BuyAdminUserAdminResponse<TData> {
  data: TData[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
  };
  summary?: BuyAdminUserAdminSummary;
}

export interface BuyAdminActionAudit {
  id: number;
  entity_type: string;
  entity_id: string;
  action: string;
  old_value: Record<string, unknown> | null;
  new_value: Record<string, unknown> | null;
  changed_by: string | null;
  changed_role: string | null;
  note: string | null;
  created_at: string;
}

export interface BuyAdminUserAdminFilters {
  limit: number;
  offset: number;
  search?: string;
  membershipStatus?: string;
  timeframe?: string;
}

export interface BuyAdminPerformerTeamCatalogEntry {
  name: string;
  category_name: string;
}

export interface BuyAdminPerformerTeamCatalogResponse {
  data: BuyAdminPerformerTeamCatalogEntry[];
}

export interface BuyAdminTicketSavingsRecord {
  id: number;
  league: string;
  team: string | null;
  low: number | null;
  high: number | null;
  median: number | null;
  recommended: string;
  show_to_user_min: number;
  show_to_user_max: number;
  price_range_min: number;
  price_range_max: number | null;
  price_range_max_is_infinite: boolean;
  created_by: string | null;
  created_at: string;
  updated_by: string | null;
  updated_at: string;
}

export interface BuyAdminTicketSavingsInput {
  league: string;
  team: string;
  low: number | null;
  high: number | null;
  median: number | null;
  recommended: string;
  showToUserMin: number;
  showToUserMax: number;
  priceRangeMin: number;
  priceRangeMax: number | string | null;
  priceRangeMaxIsInfinite?: boolean;
}

export interface BuyAdminTicketSavingsConflict {
  id: number;
  league: string;
  team: string | null;
  low: number | null;
  high: number | null;
  median: number | null;
  recommended: string;
  showToUserMin: number;
  showToUserMax: number;
  priceRangeMin: number | null;
  priceRangeMax: number | null;
  priceRangeMaxIsInfinite: boolean;
}

export interface BuyAdminTicketSavingsConflictDetails {
  requestedRecord: {
    league: string;
    team: string | null;
    priceRangeMin: number | null;
    priceRangeMax: number | null;
    priceRangeMaxIsInfinite: boolean;
  };
  conflicts: BuyAdminTicketSavingsConflict[];
}

export interface BuyAdminTicketSavingsImportError {
  rowNumber: number;
  league: string;
  team: string | null;
  priceRangeMin: number | null;
  priceRangeMax: number | null;
  priceRangeMaxIsInfinite: boolean;
  reasons: string[];
  conflicts?: BuyAdminTicketSavingsConflict[];
}

export interface BuyAdminTicketSavingsImportResponse {
  data: {
    insertedCount: number;
    failedCount: number;
    errors: BuyAdminTicketSavingsImportError[];
  };
}

export class BuyAdminApiError extends Error {
  status: number;
  code: string | number | null;
  details: unknown;
  responseData: unknown;

  constructor(params: { message: string; status: number; code?: string | number | null; details?: unknown; responseData?: unknown }) {
    super(params.message);
    this.name = "BuyAdminApiError";
    this.status = params.status;
    this.code = params.code ?? null;
    this.details = params.details;
    this.responseData = params.responseData;
  }
}

const getBuyAdminApiBaseUrl = () => config.buy_admin_api_url.replace(/\/+$/, "");

export const getBuyAdminFooterLogoUrl = (_folder?: string) =>
  `${getBuyAdminApiBaseUrl()}/api/admin/email-templates/assets/footer-logo`;

export const getBuyAdminMascotUrl = (folder: string, cacheBust?: string | number) => {
  const base = `${getBuyAdminApiBaseUrl()}/api/admin/email-templates/${encodeURIComponent(folder)}/mascot`;
  return cacheBust != null ? `${base}?v=${cacheBust}` : base;
};

const buildQueryString = (params: object) => {
  const searchParams = new URLSearchParams();

  Object.entries(params as Record<string, unknown>).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.set(key, String(value));
    }
  });

  return searchParams.toString();
};

const buildBuyAdminApiError = (status: number, responseData: any) =>
  new BuyAdminApiError({
    status,
    message: responseData?.error?.message || responseData?.message || `Request failed with status ${status}`,
    code: responseData?.error?.code ?? null,
    details: responseData?.error?.details,
    responseData,
  });

const buyAdminGet = async <TResponse,>(path: string, params: object = {}): Promise<TResponse> => {
  const queryString = buildQueryString(params);
  const url = `${getBuyAdminApiBaseUrl()}${path}${queryString ? `?${queryString}` : ""}`;

  const response = await fetch(url, {
    method: "GET",
    cache: "no-store",
    headers: await getAuthHeaders(),
  });
  const responseData = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw buildBuyAdminApiError(response.status, responseData);
  }

  return responseData as TResponse;
};

const buyAdminPatch = async <TResponse,>(path: string, body: object = {}): Promise<TResponse> => {
  const url = `${getBuyAdminApiBaseUrl()}${path}`;

  const response = await fetch(url, {
    method: "PATCH",
    cache: "no-store",
    headers: await getAuthHeaders(),
    body: JSON.stringify(body),
  });
  const responseData = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw buildBuyAdminApiError(response.status, responseData);
  }

  return responseData as TResponse;
};

const buyAdminPut = async <TResponse,>(path: string, body: object = {}): Promise<TResponse> => {
  const url = `${getBuyAdminApiBaseUrl()}${path}`;

  const response = await fetch(url, {
    method: "PUT",
    cache: "no-store",
    headers: await getAuthHeaders(),
    body: JSON.stringify(body),
  });
  const responseData = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw buildBuyAdminApiError(response.status, responseData);
  }

  return responseData as TResponse;
};

const buyAdminPost = async <TResponse,>(path: string, body: object = {}): Promise<TResponse> => {
  const url = `${getBuyAdminApiBaseUrl()}${path}`;

  const response = await fetch(url, {
    method: "POST",
    cache: "no-store",
    headers: await getAuthHeaders(),
    body: JSON.stringify(body),
  });
  const responseData = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw buildBuyAdminApiError(response.status, responseData);
  }

  return responseData as TResponse;
};

const nodeApiPost = async <TResponse,>(path: string, body: object = {}): Promise<TResponse> => {
  const baseUrl = String(config.node_api_url || "").replace(/\/+$/, "");
  if (!baseUrl) {
    throw new Error("REACT_APP_NODE_API_URL is not configured");
  }
  const response = await fetch(`${baseUrl}${path}`, {
    method: "POST",
    cache: "no-store",
    headers: await getAuthHeaders(),
    body: JSON.stringify(body),
  });
  const responseData = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw buildBuyAdminApiError(response.status, responseData);
  }
  return responseData as TResponse;
};

const buyAdminDelete = async <TResponse,>(path: string): Promise<TResponse> => {
  const response = await fetch(`${getBuyAdminApiBaseUrl()}${path}`, {
    method: "DELETE",
    cache: "no-store",
    headers: await getAuthHeaders(),
  });
  const responseData = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw buildBuyAdminApiError(response.status, responseData);
  }
  return responseData as TResponse;
};

export const buyAdminApi = {
  updateUnifiedUserEmail: (userId: string, email: string) =>
    nodeApiPost<{ message: string; email?: string }>("/api/updateUserEmail", {
      input: { userId, email },
    }),
  createBuyAdminMember: (input: BuyAdminCreateMemberInput) =>
    buyAdminPost<BuyAdminCreateMemberResponse>("/api/admin/user-admin-management/customers", {
      firstName: input.firstName,
      lastName: input.lastName,
      email: input.email,
      phoneNumber: input.phoneNumber,
      password: input.password,
      streetAddress: input.streetAddress,
      city: input.city,
      state: input.state,
      zipCode: input.zipCode,
      referralCode: input.referralCode || undefined,
      sellUserId: input.sellUserId || undefined,
      validateOnly: input.validateOnly || undefined,
    }),
  /** Sell Add User's fallback when the adminCreateUser action is unavailable. */
  createSellUserViaNodeApi: (input: SellStaffCreateUserInput) =>
    nodeApiPost<{ message?: string; user?: { u_id?: string } }>("/api/staffCreateUser", {
      input: { ...input, directAffiliateId: null, indirectAffiliateId: null },
    }),
  getEmailNotifications: (filters: BuyEmailNotificationFilters) =>
    buyAdminGet<BuyEmailNotificationsResponse>("/api/admin/email-notifications", filters),
  getEmailNotificationStatuses: () =>
    buyAdminGet<BuyEmailNotificationStatusesResponse>("/api/admin/email-notifications/statuses"),
  getEmailNotificationOrderStatuses: () =>
    buyAdminGet<BuyEmailNotificationStatusesResponse>("/api/admin/email-notifications/order-statuses"),
  getOrderFinancials: (filters: BuyOrderFinancialsFilters) =>
    buyAdminGet<BuyOrderFinancialsResponse>("/api/admin/order-financials", filters),
  getBuyAnalytics: (filters: BuyAnalyticsFilters) =>
    buyAdminGet<BuyAnalyticsResponse>("/api/admin/buy-analytics", filters),
  getPricingIntelligence: (filters: BuyPricingIntelligenceFilters = {}) =>
    buyAdminGet<BuyPricingIntelligenceResponse>("/api/admin/pricing-intelligence", filters),
  getRevenueReports: (filters: BuyRevenueReportsFilters = {}) =>
    buyAdminGet<BuyRevenueReportsResponse>("/api/admin/revenue-reports", filters),
  getAllPurchases: (filters: BuyAllPurchasesFilters = {}) =>
    buyAdminGet<BuyAllPurchasesResponse>("/api/admin/all-purchases", filters),
  getFailedPurchases: (filters: BuyFailedPurchasesFilters = {}) =>
    buyAdminGet<BuyFailedPurchasesResponse>("/api/admin/failed-purchases", filters),
  getRenewalFailures: (filters: BuyRenewalFailuresFilters = {}) =>
    buyAdminGet<BuyRenewalFailuresResponse>("/api/admin/renewal-failures", filters),
  getFulfillmentOrders: (filters: BuyFulfillmentFilters) =>
    buyAdminGet<BuyFulfillmentResponse>("/api/admin/fulfillment/orders", filters),
  updateFulfillmentOrderStatus: (orderId: string, body: { status: string; changed_by?: string; changedBy?: string }) =>
    buyAdminPatch<{ id: string; order_number: string | null; status: string; status_history: unknown[] }>(
      `/api/admin/fulfillment/orders/${encodeURIComponent(orderId)}/status`,
      body,
    ),
  bulkUpdateFulfillmentOrderStatus: (body: { order_ids: string[]; status: string; changed_by?: string; changedBy?: string }) =>
    buyAdminPost<{ updated: number; status: string }>("/api/admin/fulfillment/orders/bulk-status", body),
  getEventsMarkup: (filters: BuyAdminEventsMarkupFilters) =>
    buyAdminGet<BuyAdminEventsMarkupResponse>("/api/admin/events-markup", filters),
  getLandingComparisonSelection: () =>
    buyAdminGet<BuyAdminLandingComparisonSelectionResponse>("/api/admin/events-markup/landing-comparison"),
  updateLandingComparisonSelection: (
    configurations: BuyAdminCarouselConfigurationInput[],
    actor?: { changedBy?: string; role?: string; note?: string },
  ) =>
    buyAdminPut<BuyAdminLandingComparisonSelectionResponse>("/api/admin/events-markup/landing-comparison", {
      configurations,
      ...actor,
    }),
  updateEventMarkup: (eventId: number, body: { markupPercentage: number; changedBy?: string; role?: string; note?: string }) =>
    buyAdminPatch<{ data: { event_id: number; event_name: string; old_markup_percentage: number; markup_percentage: number } }>(
      `/api/admin/events-markup/${eventId}`,
      body,
    ),
  bulkUpdateUpcomingEventMarkup: (body: { markupPercentage: number; changedBy?: string; role?: string; note?: string }) =>
    buyAdminPatch<{ data: { markup_percentage: number; matched_count: number; updated_count: number } }>(
      "/api/admin/events-markup/bulk",
      body,
    ),
  getEventMarkupAudit: (eventId: number) =>
    buyAdminGet<BuyAdminEventMarkupAuditResponse>(`/api/admin/events-markup/${eventId}/audit`),
  getEventListings: (eventId: number) =>
    buyAdminGet<BuyAdminEventListingsResponse>("/api/v9/listings", { event_id: eventId }),
  updateEventListingMarkup: (
    eventId: number,
    listingId: number,
    body: { markupPercentage: number; changedBy?: string; role?: string; note?: string },
  ) =>
    buyAdminPut<{ data: { id: number; event_id: number; listing_id: number; markup_percentage: number; price_adjustment_amount: number } }>(
      `/api/admin/events-markup/${eventId}/listings/${listingId}`,
      body,
    ),
  getSubscriptions: () => buyAdminGet<BuyAdminSubscriptionsResponse>("/api/admin/subscriptions"),
  createSubscription: (body: BuyAdminSubscriptionInput) =>
    buyAdminPost<{ data: BuyAdminSubscription }>("/api/admin/subscriptions", body),
  updateSubscription: (id: number, body: Partial<BuyAdminSubscriptionInput>) =>
    buyAdminPatch<{ data: BuyAdminSubscription }>(`/api/admin/subscriptions/${id}`, body),
  getPerformerTeams: (filters: { q?: string; page?: number; per_page?: number }) =>
    buyAdminGet<BuyAdminPerformerTeamsListResponse>("/api/admin/performer-teams", filters),
  getPerformerTeamCatalog: () =>
    buyAdminGet<BuyAdminPerformerTeamCatalogResponse>("/api/admin/performer-teams/catalog"),
  searchPerformerTeams: (q: string) =>
    buyAdminGet<BuyAdminPerformerTeamsSearchResponse>("/api/admin/performer-teams/search", { q }),
  bulkUpdatePerformerTeams: (body: { performerIds: number[]; is_team: boolean }) =>
    buyAdminPatch<BuyAdminPerformerTeamsBulkUpdateResponse>("/api/admin/performer-teams/bulk", body),
  getTicketSavings: () =>
    buyAdminGet<{ data: BuyAdminTicketSavingsRecord[] }>("/api/admin/ticket-savings"),
  createTicketSavings: (body: BuyAdminTicketSavingsInput) =>
    buyAdminPost<{ data: BuyAdminTicketSavingsRecord }>("/api/admin/ticket-savings", body),
  updateTicketSavings: (id: number, body: BuyAdminTicketSavingsInput) =>
    buyAdminPatch<{ data: BuyAdminTicketSavingsRecord }>(`/api/admin/ticket-savings/${id}`, body),
  deleteTicketSavings: (id: number) =>
    buyAdminDelete<{ data: { id: number } }>(`/api/admin/ticket-savings/${id}`),
  importTicketSavings: (body: { rows: Array<Record<string, unknown>> }) =>
    buyAdminPost<BuyAdminTicketSavingsImportResponse>("/api/admin/ticket-savings/import", body),
  getUserAdminCustomers: (filters: BuyAdminUserAdminFilters) =>
    buyAdminGet<BuyAdminUserAdminResponse<BuyAdminManagedUser>>("/api/admin/user-admin-management/customers", filters),
  getBuyAdminSalesReps: () => buyAdminGet<{ data: BuyAdminSalesRep[] }>("/api/admin/user-admin-management/sales-reps"),
  assignBuyMemberSalesRep: (userId: string, salesRepId: string | null) =>
    buyAdminPatch<{ data: { sales_rep_id: string | null; sales_rep_name: string | null } }>(
      `/api/admin/user-admin-management/customers/${encodeURIComponent(userId)}/sales-rep`, { salesRepId }),
  getUserAdminAdmins: (filters: BuyAdminUserAdminFilters) =>
    buyAdminGet<BuyAdminUserAdminResponse<BuyAdminManagedUser>>("/api/admin/user-admin-management/admins", filters),
  getUserAdminAffiliates: (filters: BuyAdminUserAdminFilters) =>
    buyAdminGet<BuyAdminUserAdminResponse<BuyAdminAffiliate>>("/api/admin/user-admin-management/affiliates", filters),
  getAffiliateReferralUsers: (affiliateId: string, filters: BuyAdminUserAdminFilters) =>
    buyAdminGet<BuyAdminUserAdminResponse<BuyAdminAffiliateReferralUser>>(
      `/api/admin/user-admin-management/affiliates/${encodeURIComponent(affiliateId)}/referrals`,
      filters,
    ),
  createBuyAdminAffiliate: (body: {
    userId?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    organization?: string;
    affiliateType?: string;
    referredByAffiliateId?: string;
    urlSuffix?: string;
    note?: string;
    changedBy?: string;
    role?: string;
  }) => buyAdminPost<{ data: BuyAdminAffiliate; email_delivery?: BuyAdminAffiliateEmailDelivery }>(
    "/api/admin/user-admin-management/affiliates",
    body,
  ),
  removeBuyAdminAffiliate: (affiliateId: string, body: { note?: string; changedBy?: string; role?: string }) =>
    buyAdminPost<{ data: { id: string; removed: boolean } }>(
      `/api/admin/user-admin-management/affiliates/${encodeURIComponent(affiliateId)}/remove`,
      body,
    ),
  updateBuyAdminUserStatus: (userId: string, body: { isActive: boolean; note?: string; changedBy?: string; role?: string }) =>
    buyAdminPatch<{ data: { id: string; is_active: boolean; is_deactivated: boolean; deactivated_at: string | null } }>(
      `/api/admin/user-admin-management/customers/${encodeURIComponent(userId)}/status`,
      body,
    ),
  endBuyAdminCompMembership: (userId: string) =>
    buyAdminPost<{ data: { user_id: string; comp_ended: boolean; is_verified: boolean; login_code_required: boolean } }>(
      `/api/admin/user-admin-management/customers/${encodeURIComponent(userId)}/end-comp`, {},
    ),
  extendBuyAdminMembership: (userId: string, body: { endDate: string; expectedEndDate: string | null }) =>
    buyAdminPatch<{ data: { user_id: string; membership_end_at: string } }>(
      `/api/admin/user-admin-management/customers/${encodeURIComponent(userId)}/membership-extension`, body,
    ),
  updateBuyAdminUserInfo: (userId: string, body: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    note?: string;
    changedBy?: string;
    role?: string;
  }) =>
    buyAdminPatch<{ data: BuyAdminManagedUser }>(
      `/api/admin/user-admin-management/customers/${encodeURIComponent(userId)}`,
      body,
    ),
  checkBuyAdminUserEmailAvailability: (userId: string, email: string) =>
    buyAdminGet<{ data: { available: boolean; database: "buy" } }>(
      `/api/admin/user-admin-management/customers/${encodeURIComponent(userId)}/email-availability`,
      { email },
    ),
  getBuyAdminUserPaymentMethod: (userId: string) =>
    buyAdminGet<{ data: BuyAdminUserPaymentMethod | null }>(
      `/api/admin/user-admin-management/customers/${encodeURIComponent(userId)}/payment-method`,
    ),
  updateBuyAdminUserPaymentMethod: (userId: string, body: {
    cardBrand: string;
    cardLast4: string;
    cardExpiry: string;
    cardholderName: string;
    note?: string;
    changedBy?: string;
    role?: string;
  }) =>
    buyAdminPatch<{ data: BuyAdminUserPaymentMethod }>(
      `/api/admin/user-admin-management/customers/${encodeURIComponent(userId)}/payment-method`,
      body,
    ),
  getBuyAdminActionAudit: (filters: BuyAdminUserAdminFilters) =>
    buyAdminGet<BuyAdminUserAdminResponse<BuyAdminActionAudit>>("/api/admin/user-admin-management/audit", filters),
};

export type EmailTemplateView = "desktop";

export interface BuyEmailTemplateListItem {
  folder: string;
  metadata: {
    templateKey?: string;
    templateName?: string;
    subject?: string;
    variables?: string[];
    source?: string;
  } | null;
  mascotAssetKey: string | null;
  ready: boolean;
  lastModified: string | null;
}

export interface BuyEmailTemplateDetail {
  folder: string;
  metadata: BuyEmailTemplateListItem["metadata"];
  hasDesktop: boolean;
  mascotUrl: string | null;
}

export interface BuyEmailTemplatePreview {
  folder: string;
  view: EmailTemplateView;
  subject: string;
  html: string;
  htmlTemplate?: string;
  subjectTemplate?: string;
  sampleVariables: Record<string, string>;
  templateSource?: "local" | "s3";
  usesLocalTemplates?: boolean;
  localTemplatesDir?: string | null;
}

export interface BuyEmailTemplateListResponse {
  data: BuyEmailTemplateListItem[];
  meta?: {
    usesLocalTemplates?: boolean;
    localTemplatesDir?: string | null;
  };
}

const getAuthHeadersWithoutContentType = async (): Promise<HeadersInit> => {
  const headers = await getAuthHeaders();
  delete headers["Content-Type"];
  return headers;
};

const buyAdminUpload = async <TResponse,>(
  path: string,
  formData: FormData,
): Promise<TResponse> => {
  const url = `${getBuyAdminApiBaseUrl()}${path}`;
  const response = await fetch(url, {
    method: "POST",
    cache: "no-store",
    headers: await getAuthHeadersWithoutContentType(),
    body: formData,
  });
  const responseData = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(responseData?.error?.message || responseData?.message || `Request failed with status ${response.status}`);
  }
  return responseData as TResponse;
};

export const buyAdminTemplateApi = {
  getEmailTemplates: () =>
    buyAdminGet<BuyEmailTemplateListResponse>("/api/admin/email-templates"),
  getEmailTemplate: (folder: string) =>
    buyAdminGet<{ data: BuyEmailTemplateDetail }>(`/api/admin/email-templates/${encodeURIComponent(folder)}`),
  getEmailTemplatePreview: (folder: string, view: EmailTemplateView) =>
    buyAdminGet<{ data: BuyEmailTemplatePreview }>(
      `/api/admin/email-templates/${encodeURIComponent(folder)}/preview`,
      { view },
    ),
  getEmailTemplateMascotUrl: (folder: string) =>
    Promise.resolve({ data: { url: getBuyAdminMascotUrl(folder, Date.now()) } }),
  fetchEmailMascotBlob: async (folder: string): Promise<Blob | null> => {
    const response = await fetch(getBuyAdminMascotUrl(folder, Date.now()), {
      method: "GET",
      cache: "no-store",
      headers: await getAuthHeaders(),
    });
    if (response.status === 404) return null;
    if (!response.ok) {
      throw new Error(`Failed to load mascot (${response.status})`);
    }
    return response.blob();
  },
  fetchEmailFooterLogoBlob: async (folder?: string): Promise<Blob> => {
    const response = await fetch(getBuyAdminFooterLogoUrl(folder), {
      method: "GET",
      cache: "no-store",
      headers: await getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(`Failed to load footer logo (${response.status})`);
    }
    return response.blob();
  },
  uploadEmailTemplateHtml: (folder: string, view: EmailTemplateView, file: File | Blob, fileName = "desktop.html") => {
    const formData = new FormData();
    formData.append("file", file, fileName);
    formData.append("view", view);
    return buyAdminUpload<{ data: { folder: string; view: EmailTemplateView; key: string } }>(
      `/api/admin/email-templates/${encodeURIComponent(folder)}/upload-html`,
      formData,
    );
  },
  uploadEmailTemplateMascot: (folder: string, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return buyAdminUpload<{ data: { folder: string; key: string } }>(
      `/api/admin/email-templates/${encodeURIComponent(folder)}/upload-mascot`,
      formData,
    );
  },
  saveEmailTemplateSubject: (folder: string, subject: string) =>
    buyAdminPost<{ data: { folder: string; subject: string; key: string } }>(
      `/api/admin/email-templates/${encodeURIComponent(folder)}/save-subject`,
      { subject },
    ),
};
